/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */
import type { Agreement } from '../models/Agreement';
import type { AgreementBase } from '../models/AgreementBase';
import type { AppleAuthentication } from '../models/AppleAuthentication';
import type { AuthMagicLinkCreateRequest } from '../models/AuthMagicLinkCreateRequest';
import type { Body_claim_password_v1_user_claim_password_post } from '../models/Body_claim_password_v1_user_claim_password_post';
import type { Body_forgot_password_v1_user_forgot_password_post } from '../models/Body_forgot_password_v1_user_forgot_password_post';
import type { Body_logout_v1_user_logout_post } from '../models/Body_logout_v1_user_logout_post';
import type { Body_request_join_to_workspace_v1_user_workspace_join_requests_post } from '../models/Body_request_join_to_workspace_v1_user_workspace_join_requests_post';
import type { Body_set_password_v1_user_set_password_post } from '../models/Body_set_password_v1_user_set_password_post';
import type { Body_set_password_verify_v1_user_set_password_verify_post } from '../models/Body_set_password_verify_v1_user_set_password_verify_post';
import type { Body_signup_v1_user_post } from '../models/Body_signup_v1_user_post';
import type { Body_signup_verify_v1_user_signup_verify_post } from '../models/Body_signup_verify_v1_user_signup_verify_post';
import type { Body_update_locale_v1_user_locale_patch } from '../models/Body_update_locale_v1_user_locale_patch';
import type { callabo__schemas__device__Device } from '../models/callabo__schemas__device__Device';
import type { CreatedTrustedDevice } from '../models/CreatedTrustedDevice';
import type { DeviceSession } from '../models/DeviceSession';
import type { EmailAuthentication } from '../models/EmailAuthentication';
import type { GoogleAuthentication } from '../models/GoogleAuthentication';
import type { LoginResponse } from '../models/LoginResponse';
import type { MagicLinkCreateResponse } from '../models/MagicLinkCreateResponse';
import type { MicrosoftAuthentication } from '../models/MicrosoftAuthentication';
import type { MicrosoftOldAuthentication } from '../models/MicrosoftOldAuthentication';
import type { PostTrustedDeviceRequest } from '../models/PostTrustedDeviceRequest';
import type { PushDeviceRequest } from '../models/PushDeviceRequest';
import type { RefreshAuthentication } from '../models/RefreshAuthentication';
import type { Requires2FAResponse } from '../models/Requires2FAResponse';
import type { ScopedTokenAuthentication } from '../models/ScopedTokenAuthentication';
import type { SharedRecordAuthentication } from '../models/SharedRecordAuthentication';
import type { SharedRecordAuthResponse } from '../models/SharedRecordAuthResponse';
import type { SuggestedWorkspace } from '../models/SuggestedWorkspace';
import type { TrustedDevice } from '../models/TrustedDevice';
import type { TrustedDeviceVerificationStartResponse } from '../models/TrustedDeviceVerificationStartResponse';
import type { UpdateDeviceRequest } from '../models/UpdateDeviceRequest';
import type { User } from '../models/User';
import type { UserExists } from '../models/UserExists';
import type { UserExistsRequest } from '../models/UserExistsRequest';
import type { UserUpdateRequest } from '../models/UserUpdateRequest';

import type { CancelablePromise } from '../core/CancelablePromise';
import type { BaseHttpRequest } from '../core/BaseHttpRequest';

export class UserService {
  constructor(public readonly httpRequest: BaseHttpRequest) {}

  /**
   * Auth
   * `refresh_token`은 사용시 무효화됨(=1회용)
   * @param requestBody
   * @param xDeviceId
   * @param xTrustToken
   * @returns any Successful Response
   * @throws ApiError
   */
  public authV1UserAuthPost(
    requestBody:
      | EmailAuthentication
      | GoogleAuthentication
      | AppleAuthentication
      | MicrosoftOldAuthentication
      | MicrosoftAuthentication
      | SharedRecordAuthentication
      | RefreshAuthentication
      | ScopedTokenAuthentication,
    xDeviceId?: string | null,
    xTrustToken?: string | null
  ): CancelablePromise<LoginResponse | SharedRecordAuthResponse | Requires2FAResponse> {
    return this.httpRequest.request({
      method: 'POST',
      url: '/v1/user/auth',
      headers: {
        'X-Device-Id': xDeviceId,
        'X-Trust-Token': xTrustToken,
      },
      body: requestBody,
      mediaType: 'application/json',
      errors: {
        409: `Conflict`,
        422: `Validation Error`,
      },
    });
  }

  /**
   * Auth Create Magic Link
   * @param requestBody
   * @returns MagicLinkCreateResponse Successful Response
   * @throws ApiError
   */
  public authCreateMagicLinkV1UserAuthMagicLinkPost(
    requestBody: AuthMagicLinkCreateRequest
  ): CancelablePromise<MagicLinkCreateResponse> {
    return this.httpRequest.request({
      method: 'POST',
      url: '/v1/user/auth/magic-link',
      body: requestBody,
      mediaType: 'application/json',
      errors: {
        422: `Validation Error`,
      },
    });
  }

  /**
   * Who Am I
   * @returns User Successful Response
   * @throws ApiError
   */
  public whoAmIV1UserMeGet(): CancelablePromise<User> {
    return this.httpRequest.request({
      method: 'GET',
      url: '/v1/user/me',
    });
  }

  /**
   * Signup
   * @param requestBody
   * @returns LoginResponse Successful Response
   * @throws ApiError
   */
  public signupV1UserPost(requestBody: Body_signup_v1_user_post): CancelablePromise<LoginResponse> {
    return this.httpRequest.request({
      method: 'POST',
      url: '/v1/user',
      body: requestBody,
      mediaType: 'application/json',
      errors: {
        409: `Conflict`,
        422: `Validation Error`,
      },
    });
  }

  /**
   * Withdraw
   * @returns User Successful Response
   * @throws ApiError
   */
  public withdrawV1UserDelete(): CancelablePromise<User> {
    return this.httpRequest.request({
      method: 'DELETE',
      url: '/v1/user',
      errors: {
        412: `Precondition Failed`,
      },
    });
  }

  /**
   * Modify
   * @param requestBody
   * @returns User Successful Response
   * @throws ApiError
   */
  public modifyV1UserPatch(requestBody: UserUpdateRequest): CancelablePromise<User> {
    return this.httpRequest.request({
      method: 'PATCH',
      url: '/v1/user',
      body: requestBody,
      mediaType: 'application/json',
      errors: {
        422: `Validation Error`,
      },
    });
  }

  /**
   * Logout
   * `push_token`을 등록한적이 없다면, 빈 바디(`{}`)로 호출
   * @param xDeviceId
   * @param requestBody
   * @returns void
   * @throws ApiError
   */
  public logoutV1UserLogoutPost(
    xDeviceId?: string | null,
    requestBody?: Body_logout_v1_user_logout_post
  ): CancelablePromise<void> {
    return this.httpRequest.request({
      method: 'POST',
      url: '/v1/user/logout',
      headers: {
        'X-Device-Id': xDeviceId,
      },
      body: requestBody,
      mediaType: 'application/json',
      errors: {
        422: `Validation Error`,
      },
    });
  }

  /**
   * Revoke All My Tokens
   * 자신의 모든 JWT 토큰을 무효화합니다 (모든 기기에서 로그아웃).
   * @returns void
   * @throws ApiError
   */
  public revokeAllMyTokensV1UserRevokeAllTokensPost(): CancelablePromise<void> {
    return this.httpRequest.request({
      method: 'POST',
      url: '/v1/user/revoke-all-tokens',
    });
  }

  /**
   * Update Locale
   * @param requestBody
   * @returns User Successful Response
   * @throws ApiError
   */
  public updateLocaleV1UserLocalePatch(
    requestBody: Body_update_locale_v1_user_locale_patch
  ): CancelablePromise<User> {
    return this.httpRequest.request({
      method: 'PATCH',
      url: '/v1/user/locale',
      body: requestBody,
      mediaType: 'application/json',
      errors: {
        422: `Validation Error`,
      },
    });
  }

  /**
   * Signup Verify
   * @param requestBody
   * @returns void
   * @throws ApiError
   */
  public signupVerifyV1UserSignupVerifyPost(
    requestBody: Body_signup_verify_v1_user_signup_verify_post
  ): CancelablePromise<void> {
    return this.httpRequest.request({
      method: 'POST',
      url: '/v1/user/signup/verify',
      body: requestBody,
      mediaType: 'application/json',
      errors: {
        412: `Precondition Failed`,
        422: `Validation Error`,
      },
    });
  }

  /**
   * Set Password Verify
   * @param requestBody
   * @returns void
   * @throws ApiError
   */
  public setPasswordVerifyV1UserSetPasswordVerifyPost(
    requestBody: Body_set_password_verify_v1_user_set_password_verify_post
  ): CancelablePromise<void> {
    return this.httpRequest.request({
      method: 'POST',
      url: '/v1/user/set-password/verify',
      body: requestBody,
      mediaType: 'application/json',
      errors: {
        404: `There is no user with this email or withdrawn`,
        412: `Precondition Failed`,
        422: `Validation Error`,
      },
    });
  }

  /**
   * Set Password
   * @param requestBody
   * @returns LoginResponse Successful Response
   * @throws ApiError
   */
  public setPasswordV1UserSetPasswordPost(
    requestBody: Body_set_password_v1_user_set_password_post
  ): CancelablePromise<LoginResponse> {
    return this.httpRequest.request({
      method: 'POST',
      url: '/v1/user/set-password',
      body: requestBody,
      mediaType: 'application/json',
      errors: {
        404: `Not Found`,
        422: `Validation Error`,
      },
    });
  }

  /**
   * Forgot Password
   * @param requestBody
   * @returns void
   * @throws ApiError
   */
  public forgotPasswordV1UserForgotPasswordPost(
    requestBody: Body_forgot_password_v1_user_forgot_password_post
  ): CancelablePromise<void> {
    return this.httpRequest.request({
      method: 'POST',
      url: '/v1/user/forgot-password',
      body: requestBody,
      mediaType: 'application/json',
      errors: {
        403: `Forbidden`,
        404: `Not Found`,
        422: `Validation Error`,
      },
    });
  }

  /**
   * Claim Password
   * @param requestBody
   * @returns User Successful Response
   * @throws ApiError
   */
  public claimPasswordV1UserClaimPasswordPost(
    requestBody: Body_claim_password_v1_user_claim_password_post
  ): CancelablePromise<User> {
    return this.httpRequest.request({
      method: 'POST',
      url: '/v1/user/claim-password',
      body: requestBody,
      mediaType: 'application/json',
      errors: {
        404: `Not Found`,
        422: `Validation Error`,
      },
    });
  }

  /**
   * Acceptance
   * @returns Agreement Successful Response
   * @throws ApiError
   */
  public acceptanceV1UserAcceptanceGet(): CancelablePromise<Array<Agreement>> {
    return this.httpRequest.request({
      method: 'GET',
      url: '/v1/user/acceptance',
    });
  }

  /**
   * Accept Term
   * @param requestBody
   * @returns Agreement Successful Response
   * @throws ApiError
   */
  public acceptTermV1UserAcceptancePatch(
    requestBody: Array<AgreementBase>
  ): CancelablePromise<Array<Agreement>> {
    return this.httpRequest.request({
      method: 'PATCH',
      url: '/v1/user/acceptance',
      body: requestBody,
      mediaType: 'application/json',
      errors: {
        422: `Validation Error`,
      },
    });
  }

  /**
   * Push Token
   * - 내용이 같더라도 주기적으로 호출해주십시오.
   * - Web버전의 경우 login시, token refresh때 호출합니다.
   * - 마지막 호출로 부터 2개월 지날 경우 비활성화된 Device로 판단합니다.
   * - POST /v1/user/logout에 device 인자를 주면 비활성화 처리 됩니다.
   * @param requestBody
   * @returns void
   * @throws ApiError
   */
  public pushTokenV1UserPushTokenPost(requestBody: PushDeviceRequest): CancelablePromise<void> {
    return this.httpRequest.request({
      method: 'POST',
      url: '/v1/user/push-token',
      body: requestBody,
      mediaType: 'application/json',
      errors: {
        422: `Validation Error`,
      },
    });
  }

  /**
   * Exists
   * @param requestBody
   * @returns UserExists Successful Response
   * @throws ApiError
   */
  public existsV1UserExistsPost(requestBody: UserExistsRequest): CancelablePromise<UserExists> {
    return this.httpRequest.request({
      method: 'POST',
      url: '/v1/user/exists',
      body: requestBody,
      mediaType: 'application/json',
      errors: {
        422: `Unprocessable Content`,
      },
    });
  }

  /**
   * Get Suggested Workspaces
   * @returns SuggestedWorkspace Successful Response
   * @throws ApiError
   */
  public getSuggestedWorkspacesV1UserMeSuggestedWorkspacesGet(): CancelablePromise<
    Array<SuggestedWorkspace>
  > {
    return this.httpRequest.request({
      method: 'GET',
      url: '/v1/user/me/suggested-workspaces',
    });
  }

  /**
   * Request Join To Workspace
   * @param requestBody
   * @returns SuggestedWorkspace Successful Response
   * @throws ApiError
   */
  public requestJoinToWorkspaceV1UserWorkspaceJoinRequestsPost(
    requestBody: Body_request_join_to_workspace_v1_user_workspace_join_requests_post
  ): CancelablePromise<Array<SuggestedWorkspace>> {
    return this.httpRequest.request({
      method: 'POST',
      url: '/v1/user/workspace-join-requests',
      body: requestBody,
      mediaType: 'application/json',
      errors: {
        404: `Not Found`,
        409: `Conflict`,
        422: `Validation Error`,
        429: `Too Many Requests`,
      },
    });
  }

  /**
   * List Trusted Devices
   * @returns TrustedDevice Successful Response
   * @throws ApiError
   */
  public listTrustedDevicesV1UserTrustedDevicesGet(): CancelablePromise<Array<TrustedDevice>> {
    return this.httpRequest.request({
      method: 'GET',
      url: '/v1/user/trusted-devices',
    });
  }

  /**
   * Get Trust Of Device
   * @param deviceId
   * @returns TrustedDevice Successful Response
   * @throws ApiError
   */
  public getTrustOfDeviceV1UserDevicesDeviceIdTrustGet(
    deviceId: string
  ): CancelablePromise<TrustedDevice> {
    return this.httpRequest.request({
      method: 'GET',
      url: '/v1/user/devices/{device_id}/trust',
      path: {
        device_id: deviceId,
      },
      errors: {
        422: `Validation Error`,
      },
    });
  }

  /**
   * Trust Device
   * @param deviceId
   * @param requestBody
   * @returns CreatedTrustedDevice Successful Response
   * @throws ApiError
   */
  public trustDeviceV1UserDevicesDeviceIdTrustPost(
    deviceId: string,
    requestBody: PostTrustedDeviceRequest
  ): CancelablePromise<CreatedTrustedDevice> {
    return this.httpRequest.request({
      method: 'POST',
      url: '/v1/user/devices/{device_id}/trust',
      path: {
        device_id: deviceId,
      },
      body: requestBody,
      mediaType: 'application/json',
      errors: {
        422: `Validation Error`,
      },
    });
  }

  /**
   * Untrust Device
   * @param deviceId
   * @returns void
   * @throws ApiError
   */
  public untrustDeviceV1UserDevicesDeviceIdTrustDelete(deviceId: string): CancelablePromise<void> {
    return this.httpRequest.request({
      method: 'DELETE',
      url: '/v1/user/devices/{device_id}/trust',
      path: {
        device_id: deviceId,
      },
      errors: {
        422: `Validation Error`,
      },
    });
  }

  /**
   * Update Device
   * @param deviceId
   * @param requestBody
   * @returns callabo__schemas__device__Device Successful Response
   * @throws ApiError
   */
  public updateDeviceV1UserDevicesDeviceIdPatch(
    deviceId: string,
    requestBody: UpdateDeviceRequest
  ): CancelablePromise<callabo__schemas__device__Device> {
    return this.httpRequest.request({
      method: 'PATCH',
      url: '/v1/user/devices/{device_id}',
      path: {
        device_id: deviceId,
      },
      body: requestBody,
      mediaType: 'application/json',
      errors: {
        422: `Validation Error`,
      },
    });
  }

  /**
   * Start Trust Verification
   * @param deviceId
   * @returns TrustedDeviceVerificationStartResponse Successful Response
   * @throws ApiError
   */
  public startTrustVerificationV1UserDevicesDeviceIdTrustVerificationsPost(
    deviceId: string
  ): CancelablePromise<TrustedDeviceVerificationStartResponse> {
    return this.httpRequest.request({
      method: 'POST',
      url: '/v1/user/devices/{device_id}/trust-verifications',
      path: {
        device_id: deviceId,
      },
      errors: {
        422: `Validation Error`,
      },
    });
  }

  /**
   * List Device Sessions
   * @returns DeviceSession Successful Response
   * @throws ApiError
   */
  public listDeviceSessionsV1UserDevicesSessionsGet(): CancelablePromise<Array<DeviceSession>> {
    return this.httpRequest.request({
      method: 'GET',
      url: '/v1/user/devices/sessions',
    });
  }

  /**
   * Logout Device Session
   * 다른 기기/세션에서 로그아웃시키기
   * @param deviceId
   * @returns void
   * @throws ApiError
   */
  public logoutDeviceSessionV1UserDevicesDeviceIdSessionDelete(
    deviceId: string
  ): CancelablePromise<void> {
    return this.httpRequest.request({
      method: 'DELETE',
      url: '/v1/user/devices/{device_id}/session',
      path: {
        device_id: deviceId,
      },
      errors: {
        422: `Validation Error`,
      },
    });
  }
}
